/**
 * Session Service
 *
 * Управляет сессией пользователя и координирует состояние между вкладками.
 * Обрабатывает предупреждения о скором истечении сессии и истечение сессии.
 */

import type { SessionBroadcastMessage } from '../types/session.types';

type SessionWarningListener = (secondsLeft: number) => void;
type SessionExpiredListener = () => void;

class SessionService {
  private channel: BroadcastChannel | null = null;
  private channelName = 'session_broadcast';
  private warningListeners = new Set<SessionWarningListener>();
  private expiredListeners = new Set<SessionExpiredListener>();
  private isWarningVisible = false;
  private logoutHandler: (() => Promise<void> | void) | null = null;

  constructor() {
    this.initBroadcastChannel();
  }

  /**
   * Инициализация BroadcastChannel для синхронизации между вкладками
   */
  private initBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = (event: MessageEvent<SessionBroadcastMessage>) => {
        this.handleBroadcastMessage(event.data);
      };
    } catch (error) {
      console.warn('[session.service] BroadcastChannel не поддерживается', error);
    }
  }

  /**
   * Обработка сообщений от других вкладок
   */
  private handleBroadcastMessage(message: SessionBroadcastMessage): void {
    switch (message.type) {
      case 'session-warning-show':
        // Показать предупреждение во всех вкладках
        this.notifyWarningListeners(message.secondsLeft);
        this.isWarningVisible = true;
        break;

      case 'session-warning-hide':
      case 'session-extended':
        // Скрыть предупреждение во всех вкладках
        this.hideWarning();
        break;

      case 'session-logout':
        // Выполнить logout во всех вкладках
        this.performLogout();
        break;

      case 'session-expired':
        // Обработать истечение сессии во всех вкладках
        this.notifyExpiredListeners();
        break;
    }
  }

  /**
   * Broadcast сообщения во все вкладки
   */
  private broadcast(message: SessionBroadcastMessage): void {
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch (error) {
        console.error('[session.service] Ошибка при отправке broadcast сообщения', error);
      }
    }
  }

  /**
   * Показать предупреждение о скором истечении сессии
   */
  showWarning(secondsLeft: number): void {
    this.isWarningVisible = true;
    this.notifyWarningListeners(secondsLeft);
    // Уведомить другие вкладки
    this.broadcast({ type: 'session-warning-show', secondsLeft });
  }

  /**
   * Скрыть предупреждение о сессии
   */
  hideWarning(): void {
    this.isWarningVisible = false;
    // Очистить слушателей предупреждений (они сами закроют модалку)
    this.warningListeners.forEach(listener => {
      try {
        listener(0); // Передаем 0 чтобы сигнализировать о закрытии
      } catch (error) {
        console.error('[session.service] Ошибка в warning listener', error);
      }
    });
  }

  /**
   * Продлить сессию (пользователь нажал "Остаться в системе")
   */
  async extendSession(): Promise<void> {
    // Скрыть предупреждение во всех вкладках
    this.hideWarning();
    this.broadcast({ type: 'session-extended' });

    console.log('[session.service] Сессия продлена');
  }

  /**
   * Logout (пользователь нажал "Выйти" или сессия истекла)
   */
  async logout(): Promise<void> {
    // Уведомить другие вкладки о logout
    this.broadcast({ type: 'session-logout' });
    // Выполнить logout в текущей вкладке
    await this.performLogout();
  }

  setLogoutHandler(handler: () => Promise<void> | void): void {
    this.logoutHandler = handler;
  }

  /**
   * Выполнить logout
   */
  private async performLogout(): Promise<void> {
    try {
      if (this.logoutHandler) {
        await this.logoutHandler();
      }
    } catch (error) {
      console.error('[session.service] Ошибка при logout', error);
    } finally {
      const loc = window.location.pathname + window.location.search + window.location.hash;
      window.location.href = `/login?redirect=${encodeURIComponent(loc)}`;
    }
  }

  /**
   * Обработать истечение сессии
   */
  handleSessionExpired(message?: string): void {
    console.warn('[session.service] Сессия истекла', message);

    // Скрыть предупреждение если оно было показано
    this.hideWarning();

    // Уведомить слушателей об истечении сессии
    this.notifyExpiredListeners();

    // Уведомить другие вкладки
    this.broadcast({ type: 'session-expired', message });

    // Выполнить logout и редирект
    void this.performLogout();
  }

  /**
   * Подписаться на события предупреждения о сессии
   */
  onWarning(listener: SessionWarningListener): () => void {
    this.warningListeners.add(listener);
    return () => {
      this.warningListeners.delete(listener);
    };
  }

  /**
   * Подписаться на события истечения сессии
   */
  onExpired(listener: SessionExpiredListener): () => void {
    this.expiredListeners.add(listener);
    return () => {
      this.expiredListeners.delete(listener);
    };
  }

  /**
   * Уведомить слушателей о предупреждении
   */
  private notifyWarningListeners(secondsLeft: number): void {
    this.warningListeners.forEach(listener => {
      try {
        listener(secondsLeft);
      } catch (error) {
        console.error('[session.service] Ошибка в warning listener', error);
      }
    });
  }

  /**
   * Уведомить слушателей об истечении сессии
   */
  private notifyExpiredListeners(): void {
    this.expiredListeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('[session.service] Ошибка в expired listener', error);
      }
    });
  }

  /**
   * Проверить видимость предупреждения
   */
  isWarningShown(): boolean {
    return this.isWarningVisible;
  }

  /**
   * Очистка ресурсов
   */
  destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.warningListeners.clear();
    this.expiredListeners.clear();
  }
}

export const sessionService = new SessionService();
export default sessionService;
